import { Box, Button, Grid, GridItem, Text } from '@chakra-ui/react';
import React, { useContext, useEffect, useState } from 'react';
import { CartContext } from '../../../../context/cart';
import useLabsStore from '../../../../store/labsStore';
import axios from 'axios';

const SlotButton = ({ slot, disabled }) => {
  const { setOrderDetail, orderDetail } = useContext(CartContext);

  return (
    <GridItem width={'100%'}>
      <Button
        disabled={disabled}
        sx={{
          width: '100%',
          px: '20px',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: '15px',
          backgroundColor:
            orderDetail?.appointmentTime === slot
              ? 'rgba(207, 255, 207, 0.8)'
              : disabled ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          _hover: {
            backgroundColor: disabled ? 'rgba(0, 0, 0, 0.05)' : 'rgba(207, 255, 207, 0.8)',
          }
        }}
        onClick={() => {
          if (disabled) return;
          setOrderDetail(orderDetail => ({
            ...orderDetail,
            appointmentTime: slot,
          }))
        }}
      >
        <Text
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            textDecoration: disabled ? 'line-through' : 'none',
            color: disabled ? 'gray.500' : 'inherit',
          }}
        >
          {slot}
        </Text>
      </Button>
    </GridItem>
  );
};

const TimeSlot = ({ batch, availableSlots }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDir: 'column',
        alignItems: 'flex-start',
        gap: '20px',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '5px',
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          style={{
            width: '20px',
            height: '20px',
          }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
          />
        </svg>
        <Text
          sx={{
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          {batch} {' Slots '}
          {batch === 'Morning' ? '(AM)' : '(PM)'}
        </Text>
      </Box>
      <Grid
        templateColumns="repeat(auto-fill, minmax(136px, 1fr))"
        gap={6}
        width={'100%'}
      >
        {availableSlots?.map((slotData, ind) => (
          <SlotButton slot={slotData.slot} disabled={slotData.disabled} key={ind} />
        ))}
      </Grid>
    </Box>
  );
};

export const SelectTime = () => {
  const [timeSlot, setTimeSlot] = useState();
  const [bookedSlots, setBookedSlots] = useState([]);
  const { orderDetail, cartData } = useContext(CartContext);
  const { labData } = useLabsStore();

  useEffect(() => {
    const fetchBookedSlots = async () => {
      if (!orderDetail?.appointmentDate) return;
      
      let labIdToUse = labData?._id;
      if (!labIdToUse && cartData?.length > 0) {
        // Try to get labId from the first item in cart
        labIdToUse = cartData[0]?.labId || cartData[0]?.lab?._id || cartData[0]?.lab;
      }

      try {
        console.log(`Web: Fetching booked slots for Lab: ${labIdToUse}, Date: ${orderDetail.appointmentDate}`);
        const res = await axios.get(`${process.env.REACT_APP_SERVER}/getBookedSlots`, {
          params: { labId: labIdToUse, date: orderDetail.appointmentDate }
        });
        console.log(`Web: Found booked slots:`, res.data.bookedSlots);
        setBookedSlots(res.data.bookedSlots || []);
      } catch (error) {
        console.error("Error fetching booked slots:", error);
      }
    };

    fetchBookedSlots();
    
    // Refresh booked slots every 30 seconds for "real-time" updates
    const interval = setInterval(fetchBookedSlots, 30000);
    
    return () => clearInterval(interval);
  }, [labData, orderDetail?.appointmentDate, cartData]);

  useEffect(() => {
    function generateTimeRangesSeparate() {
      const amTimeRanges = [];
      const pmAfternoonTimeRanges = [];
      const pmEveningTimeRanges = [];

      const now = new Date();
      const todayStr = `${now.getDate().toString().padStart(2, '0')}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getFullYear()}`;
      const isToday = orderDetail?.appointmentDate === todayStr;

      for (let hour = 10; hour < 22; hour++) {
        const h12 = hour % 12 || 12;
        const ampm = hour < 12 ? 'AM' : 'PM';
        
        const nextHour = hour + 1;
        const nextH12 = nextHour % 12 || 12;
        const nextAmpm = nextHour < 12 || nextHour === 24 ? 'AM' : 'PM';

        const startTime = `${h12.toString().padStart(2, '0')}:00 ${ampm}`;
        const endTime = `${nextH12.toString().padStart(2, '0')}:00 ${nextAmpm}`;

        const timeRange = `${startTime} - ${endTime}`;

        const normalize = (s) => s?.replace(/[^0-9a-z]/gi, '').toLowerCase();
        const isBooked = bookedSlots.some(s => normalize(s) === normalize(timeRange));
        const isPast = isToday && hour <= now.getHours();
        
        const slotData = {
          slot: timeRange,
          disabled: isBooked || isPast
        };

        // Separate into categories
        if (hour < 12) {
          amTimeRanges.push(slotData);
        } else if (hour < 16) {
          pmAfternoonTimeRanges.push(slotData);
        } else {
          pmEveningTimeRanges.push(slotData);
        }
      }

      return { am: amTimeRanges, afternoon: pmAfternoonTimeRanges, evening: pmEveningTimeRanges };
    }

    const tSlots = generateTimeRangesSeparate();
    setTimeSlot(() => tSlots);
  }, [bookedSlots, orderDetail?.appointmentDate]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDir: 'column',
        alignItems: 'flex-start',
        gap: '20px',
        maxWidth: '100%',
        width: '100%',
      }}
    >
      <Text
        sx={{
          color: 'black',
          fontSize: '18px',
          fontWeight: 600,
        }}
      >
        Select Time
      </Text>
      <Box
        sx={{
          display: 'flex',
          flexDir: 'column',
          gap: '20px',
          alignItems: 'center',
          width: '100%',
          overflowX: 'scroll',
          px: '30px',
          py: '20px',
          border: '1px solid rgba(0, 0, 0, 0.2)',
          borderRadius: '20px',
        }}
      >
        {timeSlot?.am?.length > 0 && <TimeSlot batch={'Morning'} availableSlots={timeSlot?.am} />}
        {timeSlot?.afternoon?.length > 0 && <TimeSlot batch={'Afternoon'} availableSlots={timeSlot?.afternoon} />}
        {timeSlot?.evening?.length > 0 && <TimeSlot batch={'Evening'} availableSlots={timeSlot?.evening} />}
      </Box>
    </Box>
  );
};
